<template>
  <button :class="styles" :disabled="props.disabled">
    <template v-if="isIconVariant">
      <span
        v-if="hasIconSlot"
        class="base-btn__icon inline-block w-5 h-5"
        :style="iconColorStyles"
        aria-hidden="true"
      >
        <slot name="icon" />
      </span>
      <span
        v-else-if="props.icon"
        class="base-btn__icon inline-block w-5 h-5"
        :style="[iconStyles, iconColorStyles]"
        aria-hidden="true"
      ></span>
    </template>
    <template v-else>
      <span
        v-if="hasIconSlot"
        class="base-btn__icon inline-block"
        aria-hidden="true"
      >
        <slot name="icon" />
      </span>
      <img
        v-else-if="props.icon"
        :src="props.icon"
        class="base-btn__icon inline-block w-5 h-5"
        alt=""
        aria-hidden="true"
      />
      <template v-if="props.compact && hasIcon">
        <span class="sr-only sm:hidden">
          <slot>Button</slot>
        </span>
        <span class="hidden sm:inline">
          <slot>Button</slot>
        </span>
      </template>
      <slot v-else>Button</slot>
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { iconColorKeys } from '@/components/ui/iconColors'

type BaseButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'link' | 'icon'
type BaseButtonSize = 'none' | 'sm' | 'md' | 'lg' | 'icon'

const props = withDefaults(defineProps<{
  variant?: BaseButtonVariant
  size?: BaseButtonSize
  disabled?: boolean
  compact?: boolean
  icon?: string
  iconColor?: string
  color?: string
}>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  compact: false,
  icon: '',
  iconColor: '',
  color: '',
})

const variantStyles = {
  primary: 'border border-primary bg-primary text-on-primary hover:border-primary-hover hover:bg-primary-hover',
  secondary: 'border border-secondary bg-secondary text-on-secondary hover:border-secondary-hover hover:bg-secondary-hover',
  success: 'border border-success bg-success text-on-success hover:border-success-hover hover:bg-success-hover',
  danger: 'border border-danger bg-danger text-on-danger hover:border-danger-hover hover:bg-danger-hover',
  warning: 'border border-warning bg-warning text-on-warning hover:border-warning-hover hover:bg-warning-hover',
  ghost: 'border border-transparent bg-transparent text-on-surface hover:border-border hover:bg-surface-interactive hover:text-on-surface',
  link: 'border-0 bg-transparent text-primary hover:bg-transparent hover:text-primary-hover underline-offset-4 hover:underline',
  icon: 'border border-transparent bg-transparent text-on-surface hover:border-border hover:bg-surface-interactive'
}

const ghostIconVariantStyles = 'border border-transparent bg-transparent text-on-surface hover:border-transparent hover:bg-transparent hover:text-on-surface'

const disabledVariantStyles = {
  primary: 'border border-primary-border bg-primary-subtle text-on-surface',
  secondary: 'border border-border bg-surface-interactive text-text-secondary',
  success: 'border border-success bg-surface-interactive text-on-surface',
  danger: 'border border-danger bg-danger-subtle text-on-surface',
  warning: 'border border-warning bg-surface-interactive text-on-surface',
  ghost: 'border border-transparent bg-transparent text-text-secondary',
  link: 'border-0 bg-transparent text-text-secondary underline-offset-4 underline',
  icon: 'border border-transparent bg-transparent text-text-secondary'
}

const sizeClasses = {
  none: '',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2',
  lg: 'px-5 py-3 text-base',
  icon: 'p-2 rounded-full'
}

const isIconVariant = computed(() => props.variant === 'icon')
const slots = useSlots()
const hasIconSlot = computed(() => Boolean(slots.icon))
const hasIcon = computed(() => hasIconSlot.value || Boolean(props.icon))

const iconColorToken = computed(() => {
  const requestedColor = props.color || props.iconColor || 'on-surface'
  return (iconColorKeys as readonly string[]).includes(requestedColor) ? requestedColor : 'on-surface'
})

const iconColorStyles = computed<Record<string, string>>(() => {
  const color = `var(--theme-${iconColorToken.value})`
  const styles: Record<string, string> = hasIconSlot.value ? { color } : { backgroundColor: color }
  return styles
})

const iconStyles = computed<Record<string, string>>(() => {
  if (!props.icon || !isIconVariant.value) return {} as Record<string, string>
  const maskUrl = JSON.stringify(props.icon)
  return {
    maskImage: `url(${maskUrl})`,
    WebkitMaskImage: `url(${maskUrl})`,
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center',
    maskSize: 'contain',
    WebkitMaskSize: 'contain'
  }
})

const styles = computed(() => {
  const baseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'rounded-lg',
    'font-medium',
    'transition-default',
    'duration-fast',
      'shadow-floating-low',
  ]

  if (isIconVariant.value) {
    baseStyles.push('rounded-full')
  }

  if (props.disabled) {
    baseStyles.push('opacity-70', 'cursor-default', 'shadow-none')
  } else {
    baseStyles.push(
      'cursor-pointer',
      'focus-visible:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-[var(--theme-focus-ring)]',
      'focus-visible:ring-offset-1',
      'focus-visible:ring-offset-[var(--theme-focus-ring-offset)]',
        'active:scale-[0.99]',
      'hover:shadow-interactive'
    )
  }

  const colorStyles = props.disabled
    ? (disabledVariantStyles[props.variant] || disabledVariantStyles.primary)
    : (props.variant === 'ghost' && props.size === 'icon'
      ? ghostIconVariantStyles
      : (variantStyles[props.variant] || variantStyles.primary))
  const paddingStyles = sizeClasses[props.size] ?? sizeClasses.md

  return `${baseStyles.join(' ')} ${paddingStyles} ${colorStyles} base-btn`
})
</script>
